/**
 * Edit a dish (Organizers only)
 */

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { useDish, useUpdateDish } from '@/hooks/useDishes';
import { requireOrganizer } from '@/lib/auth/routeGuards';
import { DishForm } from '@/components/dishes/DishForm';
import { Loading } from '@/components/ui/Loading';
import { ErrorMessage } from '@/components/ui/ErrorMessage';
import { useToast } from '@/components/ui/ToastProvider';
import type { CreateDishRequest } from '@/types/api';

export const Route = createFileRoute('/dishes/$dishId_/edit')({
  beforeLoad: requireOrganizer,
  component: EditDishComponent,
});

function EditDishComponent() {
  const { dishId } = Route.useParams();
  const id = parseInt(dishId, 10);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const { data: dish, isLoading, error } = useDish(id);
  const updateDish = useUpdateDish();

  const handleSubmit = async (data: CreateDishRequest) => {
    try {
      await updateDish.mutateAsync({ id, data });
      toast.success(t('dishes.updated'));
      navigate({ to: `/dishes/${id}` });
    } catch {
      toast.error(t('dishes.errorSaving'));
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Loading />
        </div>
      </div>
    );
  }

  if (error || !dish) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <ErrorMessage
            title={t('dishes.notFound')}
            message={t('dishes.notFoundDescription')}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <button
          onClick={() => navigate({ to: `/dishes/${id}` })}
          className="mb-6 text-sm font-medium text-indigo-600 hover:text-indigo-800"
        >
          ← {t('dishes.backToDish')}
        </button>
        <h1 className="text-2xl font-bold text-gray-900 mb-6">
          {t('dishes.edit')}
        </h1>
        <DishForm
          initial={dish}
          submitting={updateDish.isPending}
          onSubmit={handleSubmit}
          onCancel={() => navigate({ to: `/dishes/${id}` })}
          submitLabel={t('app.save')}
        />
      </div>
    </div>
  );
}
